import { oneTimesUpdate } from "@domain/coupons"
import useTranslate from "@hooks/use_translate"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import LoadingButton from "@mui/lab/LoadingButton"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material"
import { IAiPrompts, IAiPromptsInput } from "@pages/main/scripts/prompts"
import {
  promptLayerLabel,
  promptRowKey
} from "@pages/main/scripts/prompts/prompt.models"
import {
  EMPTY_DIFF,
  hasDiff,
  mergeDiffSummaries,
  summarizeDiff,
  type DiffSummary
} from "@pages/main/scripts/prompts/sub_components/diffSummary"
import PromptDiffPanel, {
  DiffStat
} from "@pages/main/scripts/prompts/sub_components/PromptDiffPanel"
import { showErrorToast, showSuccessToast } from "@utils/toast"
import { OneTimesType } from "__generated__/graphql"
import {
  ChangeEvent,
  DragEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react"

type PromptExportFile = {
  type: "QPienAiPromptsExport"
  version: 1
  exportedAt: string
  prompts: IAiPromptsInput[]
}

type ImportItem = {
  prompt: IAiPromptsInput
  current: IAiPrompts | null
  selected: boolean
  status: "create" | "update" | "same"
}

type DiffEntry = {
  key: string
  title: string
  oldPrompt: IAiPromptsInput | null
  newPrompt: IAiPromptsInput
  status?: ImportItem["status"]
}

type EntryDiff = {
  meta: DiffSummary
  prompt: DiffSummary
  schema: DiffSummary
  total: DiffSummary
}

const RANGE_OPTIONS = [
  { label: "All prompts", days: null },
  { label: "Last 1 month", days: 30 },
  { label: "Last 1 week", days: 7 },
  { label: "Last 3 days", days: 3 },
  { label: "Last 1 day", days: 1 }
]

function isDialogTab(value: unknown): value is "export" | "import" {
  return value === "export" || value === "import"
}

function toPromptInput(prompt: IAiPrompts): IAiPromptsInput {
  return {
    name: prompt.name,
    description: prompt.description,
    role: prompt.role ?? null,
    engineVersion: prompt.engineVersion ?? null,
    usage: prompt.usage,
    aiModel: prompt.aiModel,
    responseSchema: prompt.responseSchema ?? null,
    modelSettings: prompt.modelSettings ?? null
  }
}

function isPromptInput(value: unknown): value is IAiPromptsInput {
  const prompt = value as IAiPromptsInput

  return (
    !!prompt &&
    typeof prompt.name === "string" &&
    typeof prompt.description === "string" &&
    // `role` and `engineVersion` are optional on purpose: a file exported before either existed
    // carries neither, and importing it must still land on the shared BASE row rather than being
    // rejected.
    (prompt.role == null || typeof prompt.role === "string") &&
    (prompt.engineVersion == null ||
      typeof prompt.engineVersion === "string") &&
    typeof prompt.usage === "string" &&
    typeof prompt.aiModel === "string" &&
    (prompt.modelSettings == null ||
      (typeof prompt.modelSettings === "object" &&
        !Array.isArray(prompt.modelSettings)))
  )
}

/**
 * `modelSettings` stays absent when the file does not carry it — the prompt tooling never writes
 * it — so importing such a file keeps the row's live settings instead of wiping them.
 */
function normalizePrompt(prompt: IAiPromptsInput): IAiPromptsInput {
  return {
    name: prompt.name,
    description: prompt.description,
    role: prompt.role ?? null,
    engineVersion: prompt.engineVersion ?? null,
    usage: prompt.usage,
    aiModel: prompt.aiModel,
    responseSchema: prompt.responseSchema ?? null,
    ...(prompt.modelSettings !== undefined && {
      modelSettings: prompt.modelSettings ?? null
    })
  }
}

/** What the row will hold after import: an absent `modelSettings` keeps the current one. */
function withKeptModelSettings(
  prompt: IAiPromptsInput,
  current: IAiPrompts | null
): IAiPromptsInput {
  return prompt.modelSettings === undefined
    ? { ...prompt, modelSettings: current?.modelSettings ?? null }
    : prompt
}

function stablePromptValue(prompt: IAiPromptsInput | null) {
  if (!prompt) return ""

  return JSON.stringify(normalizePrompt(prompt))
}

function promptsAreSame(
  left: IAiPromptsInput | null,
  right: IAiPromptsInput | null
) {
  return stablePromptValue(left) === stablePromptValue(right)
}

function schemaText(prompt: IAiPromptsInput | null) {
  return prompt?.responseSchema
    ? JSON.stringify(prompt.responseSchema, null, 2)
    : ""
}

/** An absent `modelSettings` on the new side means "kept", so it shows the old value. */
function metaText(
  prompt: IAiPromptsInput | null,
  keptModelSettings?: IAiPromptsInput["modelSettings"]
) {
  if (!prompt) return ""

  return JSON.stringify(
    {
      role: prompt.role ?? null,
      usage: prompt.usage,
      aiModel: prompt.aiModel,
      modelSettings:
        prompt.modelSettings === undefined
          ? (keptModelSettings ?? null)
          : prompt.modelSettings
    },
    null,
    2
  )
}

function entryTexts(entry: DiffEntry) {
  return {
    oldMeta: metaText(entry.oldPrompt),
    newMeta: metaText(entry.newPrompt, entry.oldPrompt?.modelSettings),
    oldPrompt: entry.oldPrompt?.description ?? "",
    newPrompt: entry.newPrompt.description,
    oldSchema: schemaText(entry.oldPrompt),
    newSchema: schemaText(entry.newPrompt)
  }
}

function diffEntry(entry: DiffEntry): EntryDiff {
  const texts = entryTexts(entry)
  const meta = entry.oldPrompt
    ? summarizeDiff(texts.oldMeta, texts.newMeta)
    : EMPTY_DIFF
  const prompt = summarizeDiff(texts.oldPrompt, texts.newPrompt)
  const schema = summarizeDiff(texts.oldSchema, texts.newSchema)
  return {
    meta,
    prompt,
    schema,
    total: mergeDiffSummaries([meta, prompt, schema])
  }
}

function buildPreviousPrompt(prompt: IAiPrompts): IAiPromptsInput | null {
  const latestVersion = prompt.versions?.[0]
  if (!latestVersion) return null

  return {
    name: latestVersion.name,
    description: latestVersion.description,
    role: latestVersion.role ?? null,
    engineVersion: latestVersion.engineVersion ?? null,
    usage: latestVersion.usage,
    aiModel: latestVersion.aiModel,
    responseSchema: latestVersion.responseSchema ?? null,
    modelSettings: latestVersion.modelSettings ?? null
  }
}

function downloadJson(fileName: string, payload: PromptExportFile) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json"
  })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  URL.revokeObjectURL(url)
}

async function copyTextToClipboard(value: string) {
  if (!navigator.clipboard) {
    throw new Error("Clipboard API is not available in this browser")
  }

  await navigator.clipboard.writeText(value)
}

function parsePromptFile(raw: string): IAiPromptsInput[] {
  const json = JSON.parse(raw)
  const prompts = Array.isArray(json) ? json : json?.prompts

  if (!Array.isArray(prompts)) {
    throw new Error("Import file must contain a prompts array")
  }

  if (prompts.some((prompt) => !isPromptInput(prompt))) {
    throw new Error("Import file contains invalid prompt data")
  }

  return prompts.map(normalizePrompt)
}

function statusColor(status: ImportItem["status"]) {
  if (status === "create") return "success"
  if (status === "update") return "warning"
  return "default"
}

export default function PromptImportExportDialog({
  open,
  onClose,
  prompts,
  onImported
}: {
  open: boolean
  onClose: () => void
  prompts: IAiPrompts[]
  onImported: () => Promise<IAiPrompts[] | undefined>
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [tab, setTab] = useState<"export" | "import">("export")
  const [rangeDays, setRangeDays] = useState<number | "all">(7)
  // Row keys, not names: a name can be several rows (per role, per engine layer) and exporting
  // one of them must not drag the others along.
  const [exportSelectedKeys, setExportSelectedKeys] = useState<string[]>([])
  const [diffTarget, setDiffTarget] = useState<{
    source: "import" | "export"
    key: string
  } | null>(null)
  const [importItems, setImportItems] = useState<ImportItem[]>([])
  const [importing, setImporting] = useState(false)
  const [fileName, setFileName] = useState("")
  const [importText, setImportText] = useState("")

  // Keyed by (name, role, engineVersion): a role override, an engine version's own copy and the
  // shared BASE row all carry the same name, so a name-only map would make an imported row
  // compare against — and then overwrite — whichever of them happened to be first.
  const currentPromptMap = useMemo(() => {
    return prompts.reduce<Record<string, IAiPrompts>>((acc, prompt) => {
      acc[promptRowKey(prompt)] = prompt
      return acc
    }, {})
  }, [prompts])

  const exportPrompts = useMemo(() => {
    if (rangeDays === "all") return prompts

    const minUpdatedAt = Date.now() - rangeDays * 24 * 60 * 60 * 1000

    return prompts.filter((prompt) => {
      return new Date(prompt.updatedAt).getTime() >= minUpdatedAt
    })
  }, [prompts, rangeDays])

  const approvedExportPrompts = useMemo(() => {
    const selected = new Set(exportSelectedKeys)
    return exportPrompts.filter((prompt) => selected.has(promptRowKey(prompt)))
  }, [exportPrompts, exportSelectedKeys])

  const exportPayload = useMemo<PromptExportFile>(
    () => ({
      type: "QPienAiPromptsExport",
      version: 1,
      exportedAt: new Date().toISOString(),
      prompts: approvedExportPrompts.map(toPromptInput)
    }),
    [approvedExportPrompts]
  )

  const exportText = useMemo(() => {
    return JSON.stringify(exportPayload, null, 2)
  }, [exportPayload])

  const exportEntries = useMemo<DiffEntry[]>(
    () =>
      exportPrompts.map((prompt) => ({
        key: promptRowKey(prompt),
        title: `${prompt.name} · ${promptLayerLabel(prompt.engineVersion)}`,
        oldPrompt: buildPreviousPrompt(prompt),
        newPrompt: toPromptInput(prompt)
      })),
    [exportPrompts]
  )

  const importEntries = useMemo<DiffEntry[]>(
    () =>
      importItems.map((item) => ({
        key: promptRowKey(item.prompt),
        title: `${item.prompt.name} · ${promptLayerLabel(
          item.prompt.engineVersion
        )}`,
        oldPrompt: item.current ? toPromptInput(item.current) : null,
        newPrompt: normalizePrompt(item.prompt),
        status: item.status
      })),
    [importItems]
  )

  const exportDiffs = useMemo(
    () => new Map(exportEntries.map((entry) => [entry.key, diffEntry(entry)])),
    [exportEntries]
  )
  // Ticking a row rebuilds every item; the parsed prompt objects survive it, so the diffs do too.
  const importDiffCache = useRef(new WeakMap<IAiPromptsInput, EntryDiff>())
  const importDiffs = useMemo(
    () =>
      new Map(
        importEntries.map((entry, i) => {
          const source = importItems[i].prompt
          const cached = importDiffCache.current.get(source)
          const computed = cached ?? diffEntry(entry)
          if (!cached) importDiffCache.current.set(source, computed)
          return [entry.key, computed]
        })
      ),
    [importEntries, importItems]
  )

  useEffect(() => {
    if (!open) return

    setRangeDays(7)
    const minUpdatedAt = Date.now() - 7 * 24 * 60 * 60 * 1000
    setExportSelectedKeys(
      prompts
        .filter(
          (prompt) => new Date(prompt.updatedAt).getTime() >= minUpdatedAt
        )
        .map(promptRowKey)
    )
  }, [open, prompts])

  function resetExportSelection(nextPrompts: IAiPrompts[]) {
    setExportSelectedKeys(nextPrompts.map(promptRowKey))
  }

  function handleRangeChange(value: number | "all") {
    setRangeDays(value)
    const minUpdatedAt =
      value === "all" ? null : Date.now() - value * 24 * 60 * 60 * 1000
    const nextPrompts =
      minUpdatedAt === null
        ? prompts
        : prompts.filter((prompt) => {
            return new Date(prompt.updatedAt).getTime() >= minUpdatedAt
          })

    resetExportSelection(nextPrompts)
  }

  function toggleExportPrompt(key: string) {
    setExportSelectedKeys((prev) => {
      if (prev.includes(key)) return prev.filter((item) => item !== key)
      return [...prev, key]
    })
  }

  function exportApprovedPrompts() {
    downloadJson(
      `qpien-ai-prompts-${new Date().toISOString().slice(0, 10)}.json`,
      exportPayload
    )
  }

  function loadImportedPrompts(
    importedPrompts: IAiPromptsInput[],
    source: string
  ) {
    const nextItems = importedPrompts.map<ImportItem>((prompt) => {
      const current =
        currentPromptMap[promptRowKey(normalizePrompt(prompt))] ?? null
      const currentInput = current ? toPromptInput(current) : null
      const same = promptsAreSame(
        currentInput,
        withKeptModelSettings(normalizePrompt(prompt), current)
      )

      return {
        prompt,
        current,
        selected: !same,
        status: same ? "same" : current ? "update" : "create"
      }
    })

    setFileName(source)
    setImportItems(nextItems)
  }

  function readImportRaw(raw: string, source: string) {
    try {
      const importedPrompts = parsePromptFile(raw)
      loadImportedPrompts(importedPrompts, source)
    } catch (error) {
      showErrorToast(String(error))
      setFileName("")
      setImportItems([])
    }
  }

  async function readImportFile(file: File) {
    const raw = await file.text()
    setImportText(raw)
    readImportRaw(raw, file.name)
  }

  function handleFileInput(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (file) readImportFile(file)
    event.target.value = ""
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    const file = event.dataTransfer.files?.[0]
    if (file) readImportFile(file)
  }

  function handleImportTextLoad() {
    const trimmed = importText.trim()
    if (!trimmed) {
      showErrorToast("Paste prompt JSON text first")
      return
    }

    readImportRaw(trimmed, "Pasted text")
  }

  function toggleImportPrompt(key: string) {
    setImportItems((prev) =>
      prev.map((item) =>
        promptRowKey(item.prompt) === key
          ? { ...item, selected: !item.selected }
          : item
      )
    )
  }

  function selectAllImportItems() {
    setImportItems((prev) =>
      prev.map((item) => ({
        ...item,
        selected: item.status !== "same"
      }))
    )
  }

  async function submitImport() {
    const selectedItems = importItems.filter((item) => item.selected)
    if (!selectedItems.length) return

    setImporting(true)

    try {
      for (const item of selectedItems) {
        const isUpdate = item.status === "update"
        const params = isUpdate
          ? {
              ...normalizePrompt(item.prompt),
              name: item.current?.name
            }
          : normalizePrompt(item.prompt)
        const { isSuccess, errorMessage } = await oneTimesUpdate({
          type: isUpdate
            ? OneTimesType.UpdateAiPrompt
            : OneTimesType.CreateAiPrompt,
          metaData: params
        })

        if (!isSuccess) {
          throw new Error(`${item.prompt.name}: ${String(errorMessage)}`)
        }
      }

      await onImported()
      setImportItems([])
      setFileName("")
      setImportText("")
      showSuccessToast(`${selectedItems.length} prompt imported successfully`)
    } catch (error) {
      showErrorToast(String(error))
    } finally {
      setImporting(false)
    }
  }

  const selectedImportCount = importItems.filter((item) => item.selected).length
  const changedImportCount = importItems.filter(
    (item) => item.status !== "same"
  ).length

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
        <DialogTitle>Prompt Import / Export</DialogTitle>
        <DialogContent dividers>
          <Tabs
            value={tab}
            onChange={(_, value: unknown) => {
              if (isDialogTab(value)) setTab(value)
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="Export" value="export" />
            <Tab label="Import" value="import" />
          </Tabs>

          {tab === "export" && (
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <Select
                  size="small"
                  value={rangeDays}
                  onChange={(event) => {
                    const value = event.target.value
                    handleRangeChange(value === "all" ? value : Number(value))
                  }}
                  sx={{ minWidth: 180 }}
                >
                  {RANGE_OPTIONS.map((option) => (
                    <MenuItem key={option.label} value={option.days ?? "all"}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                <Button
                  variant="outlined"
                  onClick={() => resetExportSelection(exportPrompts)}
                >
                  Approve all
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setExportSelectedKeys([])}
                >
                  Clear
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {approvedExportPrompts.length} / {exportPrompts.length} prompt
                  selected
                </Typography>
                <Button
                  variant="outlined"
                  disabled={!approvedExportPrompts.length}
                  onClick={async () => {
                    try {
                      await copyTextToClipboard(exportText)
                      showSuccessToast("Prompt JSON copied to clipboard")
                    } catch (error) {
                      showErrorToast(String(error))
                    }
                  }}
                >
                  Copy JSON
                </Button>
              </Stack>

              <Paper
                variant="outlined"
                sx={{ maxHeight: 480, overflow: "auto" }}
              >
                <Stack divider={<Divider />}>
                  {exportPrompts.map((prompt) => {
                    const rowKey = promptRowKey(prompt)
                    const checked = exportSelectedKeys.includes(rowKey)

                    return (
                      <Stack
                        key={rowKey}
                        direction={{ xs: "column", md: "row" }}
                        spacing={1}
                        alignItems={{ xs: "stretch", md: "center" }}
                        sx={{ px: 1.5, py: 1 }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleExportPrompt(rowKey)}
                            />
                          }
                          label={
                            <Stack>
                              <Typography variant="body2" fontWeight={700}>
                                {prompt.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {promptLayerLabel(prompt.engineVersion)} •{" "}
                                {prompt.usage} • {prompt.aiModel}
                              </Typography>
                            </Stack>
                          }
                          sx={{ flex: 1, minWidth: 0 }}
                        />
                        <EntryDiffBadges diff={exportDiffs.get(rowKey)} />
                        <Typography variant="caption" color="text.secondary">
                          {new Intl.DateTimeFormat("tr-TR", {
                            dateStyle: "medium",
                            timeStyle: "short"
                          }).format(new Date(prompt.updatedAt))}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setDiffTarget({ source: "export", key: rowKey })
                          }
                        >
                          Preview diff
                        </Button>
                      </Stack>
                    )
                  })}
                  {!exportPrompts.length && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ p: 2 }}
                    >
                      No prompts found for selected range.
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <TextField
                label="Export JSON"
                multiline
                minRows={10}
                maxRows={20}
                value={exportText}
                InputProps={{ readOnly: true }}
                helperText="You can copy this JSON directly without downloading a file."
              />
            </Stack>
          )}

          {tab === "import" && (
            <Stack spacing={2}>
              <Box
                role="button"
                tabIndex={0}
                onDragOver={(event) => event.preventDefault()}
                onDrop={handleDrop}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
                sx={{
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  cursor: "pointer",
                  bgcolor: "action.hover"
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <Typography variant="body2" fontWeight={700}>
                  Drop prompt export JSON here
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {fileName || "or click to choose a file"}
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/json,.json"
                  hidden
                  onChange={handleFileInput}
                />
              </Box>

              <Stack spacing={1}>
                <TextField
                  label="Import JSON"
                  multiline
                  minRows={10}
                  maxRows={20}
                  value={importText}
                  onChange={(event) => setImportText(event.target.value)}
                  helperText="Paste exported prompt JSON here, then load it for review."
                />
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button variant="outlined" onClick={handleImportTextLoad}>
                    Load pasted JSON
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    {fileName
                      ? `Current source: ${fileName}`
                      : "No import source loaded yet"}
                  </Typography>
                </Stack>
              </Stack>

              {!!importItems.length && (
                <>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Button variant="outlined" onClick={selectAllImportItems}>
                      Approve all changes
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() =>
                        setImportItems((prev) =>
                          prev.map((item) => ({ ...item, selected: false }))
                        )
                      }
                    >
                      Clear
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {selectedImportCount} / {changedImportCount} change
                      selected
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <ImportTotals items={importItems} />
                  </Stack>

                  <Alert severity="info">
                    Existing prompts keep their role assignment while the
                    content, model, usage and schema are updated.
                  </Alert>

                  <Paper
                    variant="outlined"
                    sx={{ maxHeight: 420, overflow: "auto" }}
                  >
                    <Stack divider={<Divider />}>
                      {importItems.map((item) => (
                        <Stack
                          key={promptRowKey(item.prompt)}
                          direction={{ xs: "column", md: "row" }}
                          spacing={1}
                          alignItems={{ xs: "stretch", md: "center" }}
                          sx={{ px: 1.5, py: 1 }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={item.selected}
                                disabled={item.status === "same"}
                                onChange={() =>
                                  toggleImportPrompt(promptRowKey(item.prompt))
                                }
                              />
                            }
                            label={
                              <Stack>
                                <Typography variant="body2" fontWeight={700}>
                                  {item.prompt.name}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ ml: 0.75, opacity: 0.7 }}
                                  >
                                    {promptLayerLabel(
                                      item.prompt.engineVersion
                                    )}
                                  </Typography>
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {item.prompt.usage} • {item.prompt.aiModel}
                                </Typography>
                              </Stack>
                            }
                            sx={{ flex: 1, minWidth: 0 }}
                          />
                          <EntryDiffBadges
                            diff={importDiffs.get(promptRowKey(item.prompt))}
                          />
                          <Chip
                            label={item.status}
                            size="small"
                            color={statusColor(item.status)}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setDiffTarget({
                                source: "import",
                                key: promptRowKey(item.prompt)
                              })
                            }
                          >
                            Preview diff
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={importing}>
            Close
          </Button>
          {tab === "export" && (
            <Button
              variant="contained"
              disabled={!approvedExportPrompts.length}
              onClick={exportApprovedPrompts}
            >
              Export selected
            </Button>
          )}
          {tab === "import" && (
            <LoadingButton
              variant="contained"
              loading={importing}
              disabled={!selectedImportCount}
              onClick={submitImport}
            >
              Import selected
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      <PromptDirectDiffDialog
        entries={
          diffTarget?.source === "import" ? importEntries : exportEntries
        }
        diffs={diffTarget?.source === "import" ? importDiffs : exportDiffs}
        activeKey={diffTarget?.key ?? null}
        onNavigate={(key) =>
          setDiffTarget((prev) => (prev ? { ...prev, key } : prev))
        }
        onClose={() => setDiffTarget(null)}
        selection={
          diffTarget?.source === "import"
            ? {
                isSelected: (key) =>
                  importItems.some(
                    (item) => item.selected && promptRowKey(item.prompt) === key
                  ),
                onToggle: toggleImportPrompt
              }
            : undefined
        }
      />
    </>
  )
}

function EntryDiffBadges({ diff }: { diff?: EntryDiff }) {
  const { translate } = useTranslate()
  if (!diff) return null

  const sections = [
    { label: translate("prompt_diff_section_meta"), summary: diff.meta },
    { label: translate("prompt_diff_section_schema"), summary: diff.schema }
  ].filter((section) => hasDiff(section.summary))

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <DiffStat summary={diff.prompt} />
      {sections.map((section) => (
        <Chip
          key={section.label}
          label={section.label}
          size="small"
          variant="outlined"
          color="warning"
          sx={{ height: 20, fontSize: "0.7rem" }}
        />
      ))}
    </Stack>
  )
}

function ImportTotals({ items }: { items: ImportItem[] }) {
  const { translate } = useTranslate()
  const count = (status: ImportItem["status"]) =>
    items.filter((item) => item.status === status).length

  return (
    <Stack direction="row" spacing={0.75}>
      <Chip
        size="small"
        color="success"
        label={translate("prompt_import_count_create", {
          count: count("create")
        })}
      />
      <Chip
        size="small"
        color="warning"
        label={translate("prompt_import_count_update", {
          count: count("update")
        })}
      />
      <Chip
        size="small"
        label={translate("prompt_import_count_same", { count: count("same") })}
      />
    </Stack>
  )
}

/**
 * ‹ › (or ← →) walk the list's changed rows without closing the dialog, so a whole import can be
 * reviewed — and ticked in or out — in one pass.
 */
function PromptDirectDiffDialog({
  entries,
  diffs,
  activeKey,
  onNavigate,
  onClose,
  selection
}: {
  entries: DiffEntry[]
  diffs: Map<string, EntryDiff>
  activeKey: string | null
  onNavigate: (key: string) => void
  onClose: () => void
  selection?: {
    isSelected: (key: string) => boolean
    onToggle: (key: string) => void
  }
}) {
  const { translate } = useTranslate()
  const entry = entries.find((item) => item.key === activeKey) ?? null
  const diff = entry ? diffs.get(entry.key) : undefined
  const texts = entry ? entryTexts(entry) : null
  const focusedSection = !diff
    ? null
    : entry?.oldPrompt && hasDiff(diff.meta)
      ? "meta"
      : hasDiff(diff.prompt)
        ? "prompt"
        : hasDiff(diff.schema)
          ? "schema"
          : null

  const navigable = entries.filter(
    (item) =>
      item.key === activeKey ||
      hasDiff(diffs.get(item.key)?.total ?? EMPTY_DIFF)
  )
  const position = navigable.findIndex((item) => item.key === activeKey)
  const prev = position > 0 ? navigable[position - 1] : null
  const next =
    position >= 0 && position < navigable.length - 1
      ? navigable[position + 1]
      : null

  return (
    <Dialog
      open={!!entry}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      onKeyDown={(event) => {
        if (event.key === "ArrowLeft" && prev) onNavigate(prev.key)
        if (event.key === "ArrowRight" && next) onNavigate(next.key)
      }}
    >
      <DialogTitle>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h6" noWrap sx={{ minWidth: 0 }}>
            {entry?.title ?? "Prompt diff"}
          </Typography>
          {entry?.status && (
            <Chip
              label={entry.status}
              size="small"
              color={statusColor(entry.status)}
            />
          )}
          {diff && <DiffStat summary={diff.total} />}
          <Box sx={{ flex: 1 }} />
          <Tooltip title={translate("prompt_diff_prev_prompt")}>
            <span>
              <IconButton
                disabled={!prev}
                onClick={() => prev && onNavigate(prev.key)}
              >
                <ChevronLeftIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="body2"
            sx={{ fontFamily: "monospace", color: "text.secondary" }}
          >
            {position + 1} / {navigable.length}
          </Typography>
          <Tooltip title={translate("prompt_diff_next_prompt")}>
            <span>
              <IconButton
                disabled={!next}
                onClick={() => next && onNavigate(next.key)}
              >
                <ChevronRightIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {texts && entry && (
          <Box key={entry.key}>
            {entry.oldPrompt && (
              <PromptDiffPanel
                title={translate("prompt_diff_section_meta")}
                oldValue={texts.oldMeta}
                newValue={texts.newMeta}
                defaultExpanded={!!diff && hasDiff(diff.meta)}
                focusFirstChange={focusedSection === "meta"}
              />
            )}
            <PromptDiffPanel
              title="Prompt"
              oldValue={texts.oldPrompt}
              newValue={texts.newPrompt}
              focusFirstChange={focusedSection === "prompt"}
            />
            {(texts.oldSchema || texts.newSchema) && (
              <PromptDiffPanel
                title={translate("prompt_diff_section_schema")}
                oldValue={texts.oldSchema}
                newValue={texts.newSchema}
                defaultExpanded={!!diff && hasDiff(diff.schema)}
                focusFirstChange={focusedSection === "schema"}
              />
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {selection && entry && entry.status !== "same" && (
          <FormControlLabel
            sx={{ mr: "auto", ml: 1 }}
            control={
              <Checkbox
                checked={selection.isSelected(entry.key)}
                onChange={() => selection.onToggle(entry.key)}
              />
            }
            label={translate("prompt_import_include")}
          />
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
